import { useCallback, useEffect, useMemo } from 'react';
import { Formik, getIn, setIn } from 'formik';
import { Button } from '@mui/material';
import Card from '../../../../common/card';
import Input from '../../../../common/input';
import Select from '../../../../common/select';
import Loading from '../../../../common/loading';
import l from '../../../../../lang';
import { CreatePayoutData, FlowType, PurposeOfPayout, RequiredField } from '../../../../../store/payouts/interfaces';
import { getCreatePayoutDataValueByFieldPath, getFieldLabel, payoutPurposeOptions } from '../utils';
import { useSelector } from 'react-redux';
import { PayoutsSelectors } from '../../../../../store/payouts/selectors';
import { useCompanyInfo } from '../../../../../store/company-information';
import { invalidBankAccountCode, invalidDocumentCode } from '../../../../../utils/error-codes';
import { ReactComponent as BriefcaseIcon } from '../../../../../assets/icons/briefcase.svg';
import { operatingCountries } from '../../../../../config';
import { useDashboard } from '../../../../../store/dashboard';

interface BankAccountFormProps {
  loading: boolean;
  validating: boolean;
  fields: RequiredField[];
  bankOptions: Array<{ code: string; name: string }>;
  initialValues: Partial<CreatePayoutData>;
  backendErrorFields?: Record<string, string>;
  onBack: () => void;
  onSubmit: (values: Record<string, string | number>) => void;
}

type SelectOption = {
  value: string | null;
  label: string;
};

const bankAccountTypeOptionsMap: Record<string, SelectOption[]> = {
  AR: [
    {
      value: 'ALIAS',
      label: 'Alias',
    },
    {
      value: null,
      label: 'None',
    },
  ],
  BO: [],
  BR: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  CL: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
    {
      value: 'VISTA',
      label: 'Vista',
    },
  ],
  CO: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  DO: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  CR: [],
  UY: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
};

const BankAccountForm = ({
  loading,
  validating,
  fields,
  bankOptions,
  initialValues,
  backendErrorFields,
  onBack,
  onSubmit,
}: BankAccountFormProps) => {
  const createData = useSelector(PayoutsSelectors.getCreatePayout);
  const partialValidation = useSelector(PayoutsSelectors.getPartialValidation);
  const errorInfo = partialValidation.errorInfo;
  const { documentTypesForCompany, documentTypesForNaturalPerson, getDocumentTypesForKYC } = useCompanyInfo();
  const { merchantMe } = useDashboard();

  useEffect(() => {
    if (createData.createPayoutData.transferCountry) {
      getDocumentTypesForKYC(createData.createPayoutData.transferCountry);
    }
  }, [createData.createPayoutData.transferCountry]);

  const getDefaultValue = useCallback(
    (field: RequiredField) => {
      if (field.fieldPath === 'remitter.nationality') {
        return merchantMe?.country ?? '';
      }

      if (field.fieldPath === 'beneficiary.purpose') {
        return PurposeOfPayout.OTHER_SERVICES;
      }
      return '';
    },
    [merchantMe]
  );

  const formInitialValues = useMemo(() => {
    return fields.reduce<Record<string, any>>((accumulator, field) => {
      const mappedValue = getCreatePayoutDataValueByFieldPath(initialValues, field.fieldPath);
      const nextValue = mappedValue ?? getDefaultValue(field);
      return setIn(accumulator, field.fieldPath, nextValue);
    }, {});
  }, [fields, initialValues]);

  const initialErrors = useMemo(() => {
    if (!errorInfo) {
      return {};
    }

    if (errorInfo.code === invalidDocumentCode) {
      return {
        'beneficiary.document.id': l(errorInfo.message),
      };
    }

    if (errorInfo.code === invalidBankAccountCode) {
      return {
        'beneficiary.bank_account.account': l(errorInfo.message),
      };
    }
  }, [errorInfo]);

  const documentTypes = useMemo(() => {
    if (createData.createPayoutData.flowType === FlowType.B2B) {
      return documentTypesForCompany;
    } else {
      return documentTypesForNaturalPerson;
    }
  }, [createData.createPayoutData.flowType, documentTypesForCompany, documentTypesForNaturalPerson]);

  const bankSelectOptions = useMemo(() => {
    return bankOptions.reduce<Record<string, string>>((accumulator, option) => {
      accumulator[option.code] = option.name;
      return accumulator;
    }, {});
  }, [bankOptions]);

  const bankAccountTypeOptions = useMemo<Record<string, string>>(() => {
    const options = bankAccountTypeOptionsMap[createData.createPayoutData.transferCountry ?? ''] ?? ([] as SelectOption[]);

    if (options.length === 0) {
      return {};
    }

    return options.reduce<Record<string, string>>((accumulator, option) => {
      accumulator[option.value ?? ''] = option.label;
      return accumulator;
    }, {});
  }, [createData.createPayoutData.transferCountry]);

  const allCountriesOptions = useMemo(() => {
    return operatingCountries.reduce<Record<string, string>>((accumulator, country) => {
      accumulator[country] = l(`countryCode.${country}`);
      return accumulator;
    }, {});
  }, [operatingCountries]);

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};

    fields.forEach(field => {
      const value = getIn(values, field.fieldPath);
      const textValue = value === undefined || value === null ? '' : String(value).trim();

      if (field.required && !textValue) {
        errors[field.fieldPath] = l('form.input.required');
        return;
      }

      if (field.maxLength && textValue.length > field.maxLength) {
        errors[field.fieldPath] = l('payouts.create.error.maxLength', field.maxLength);
        return;
      }

      if (textValue && field.dataType === 'EMAIL' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(textValue)) {
        errors[field.fieldPath] = l('form.input.invalid');
      }
    });

    return errors;
  };

  return (
    <div className="payout-create__layout">
      <Card>
        <div className="payout-create__card-content">
          <h2>{l('payouts.create.accountData.bankDataTitle')}</h2>
          <br />
          <div className="transaction_detail__title_section">
            <BriefcaseIcon className="transaction_detail__title_section__icon" />
            {l('payouts.create.accountData.bankData')}
          </div>

          <Formik
            initialValues={formInitialValues}
            validate={validate}
            initialErrors={initialErrors}
            enableReinitialize
            onSubmit={values => {
              const normalizedValues = fields.reduce<Record<string, string | number>>((accumulator, field) => {
                const value = getIn(values, field.fieldPath);
                if (value !== undefined) {
                  accumulator[field.fieldPath] = value;
                }
                return accumulator;
              }, {});
              onSubmit(normalizedValues);
            }}
          >
            {({ values, errors, touched, handleBlur, setFieldValue, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                {(loading || validating) && (
                  <div className="payout-create__loading">
                    <Loading />
                  </div>
                )}
                {!loading &&
                  !validating &&
                  fields.map(field => {
                    const label = getFieldLabel(field);
                    const fieldValue = getIn(values, field.fieldPath) || '';
                    const clientError = errors[field.fieldPath];
                    const hasBackendError = backendErrorFields?.[field.fieldPath] !== undefined;
                    const hasCurrentBackendError = hasBackendError
                      ? backendErrorFields?.[field.fieldPath] === String(fieldValue)
                      : false;
                    const error = clientError ?? (hasCurrentBackendError ? l('form.input.invalid') : '');

                    if (field.fieldPath === 'beneficiary.bank_account.code') {
                      return (
                        <Select
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          defaultOptionText={l('payouts.create.field.selectPlaceholder')}
                          options={bankSelectOptions}
                          value={fieldValue as string}
                          error={getIn(touched, field.fieldPath) ? error : ''}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (field.fieldPath === 'purpose' || field.fieldPath === 'beneficiary.purpose') {
                      return (
                        <Select
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          options={payoutPurposeOptions}
                          value={fieldValue as string}
                          error={getIn(touched, field.fieldPath) ? error : ''}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (
                      field.fieldPath === 'beneficiary.document.type' &&
                      createData.createPayoutData.transferCountry &&
                      Object.keys(documentTypes).length > 0
                    ) {
                      return (
                        <Select
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          options={documentTypes}
                          value={fieldValue as string}
                          error={getIn(touched, field.fieldPath) ? error : ''}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (
                      field.fieldPath === 'beneficiary.bank_account.type' &&
                      createData.createPayoutData.transferCountry &&
                      Object.keys(bankAccountTypeOptions).length > 0
                    ) {
                      return (
                        <Select
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          options={bankAccountTypeOptions}
                          value={fieldValue as string}
                          error={getIn(touched, field.fieldPath) ? error : ''}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (field.fieldPath === 'remitter.nationality' && Object.keys(allCountriesOptions).length > 0) {
                      return (
                        <Select
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          options={allCountriesOptions}
                          value={fieldValue as string}
                          error={getIn(touched, field.fieldPath) ? error : ''}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (field.fieldPath === 'beneficiary.email') {
                      return (
                        <Input
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          type="email"
                          value={fieldValue as string}
                          error={getIn(touched, field.fieldPath) ? error : ''}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                          onFocus={() => undefined}
                        />
                      );
                    }

                    return (
                      <Input
                        key={field.fieldPath}
                        type={field.dataType === 'NUMBER' ? 'number' : field.dataType === 'DATE' ? 'date' : 'text'}
                        name={field.fieldPath}
                        label={label}
                        value={fieldValue}
                        maxLength={field.maxLength}
                        error={getIn(touched, field.fieldPath) ? error : ''}
                        onBlur={handleBlur}
                        onFocus={() => undefined}
                        onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                      />
                    );
                  })}

                <div className="payout-create__buttons">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onBack}
                    sx={{ backgroundColor: '#E8E8E8', color: 'black', ':hover': { backgroundColor: '#D0D0D0' } }}
                  >
                    {l('common.cancel')}
                  </Button>
                  <Button variant="contained" size="large" type="submit" disabled={loading || validating}>
                    {l('common.next')}
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </Card>
    </div>
  );
};

export default BankAccountForm;
